import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { X, Plus, Trash2, ClipboardCheck, Loader, AlertCircle, Copy } from "lucide-react";
import { api } from "../api";

const FIELD_TYPES = [
  { value: "compliance", label: "Cumplimiento (Cumple/No cumple/Parcial/No aplica)" },
  { value: "text", label: "Texto corto" },
  { value: "textarea", label: "Texto largo" },
  { value: "number", label: "Numero" },
  { value: "date", label: "Fecha" },
  { value: "select", label: "Seleccion (opciones)" },
  { value: "file", label: "Archivo adjunto" },
  { value: "boolean", label: "Si / No" },
];

const FOLLOW_UP_TYPES = [
  { value: "text", label: "Texto corto" },
  { value: "textarea", label: "Texto largo" },
  { value: "number", label: "Numero" },
  { value: "file", label: "Foto / archivo" },
];

function newFieldId() {
  return `f_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

function emptyFollowUp() {
  return { id: newFieldId(), label: "", type: "text", required: false };
}

function emptyField() {
  return {
    id: newFieldId(),
    label: "",
    description: "",
    type: "compliance",
    required: true,
    options: [],
    optionsText: "",
    dateCalculation: "",
    followUps: [],
  };
}

export default function InspectionBuilderModal({ inspection, onClose, onSaved }) {
  const isEdit = !!inspection;
  const [name, setName] = useState(inspection?.name || "");
  const [description, setDescription] = useState(inspection?.description || "");
  const [moduleId, setModuleId] = useState(inspection?.module_id || "");
  const [modules, setModules] = useState([]);
  useEffect(() => { api.getInspectionModules().then(data => setModules(Array.isArray(data) ? data : [])); }, []);

  const [fields, setFields] = useState(
    inspection?.fields?.length
      ? inspection.fields.map((field) => ({
          ...field,
          options: field.options || [],
          optionsText: (field.options || []).join(", "),
          dateCalculation: field.dateCalculation || "",
          followUps: field.followUps || [],
        }))
      : [emptyField()]
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [duplicateNotice, setDuplicateNotice] = useState("");

  const updateField = (id, changes) => {
    setFields((prev) => prev.map((field) => (field.id === id ? { ...field, ...changes } : field)));
  };

  const addField = () => setFields((prev) => [...prev, emptyField()]);
  const removeField = (id) => setFields((prev) => prev.filter((field) => field.id !== id));
  const duplicateField = (id) => {
    setFields((prev) => {
      const index = prev.findIndex((field) => field.id === id);
      if (index === -1) return prev;
      const source = prev[index];
      const copy = { ...source, id: newFieldId(), followUps: (source.followUps || []).map(f => ({ ...f, id: newFieldId() })) };
      return [...prev.slice(0, index + 1), copy, ...prev.slice(index + 1)];
    });
    setDuplicateNotice("ESTAS DUPLICANDO UNA PREGUNTA");
    window.setTimeout(() => setDuplicateNotice(""), 2500);
  };

  const addFollowUp = (fieldId) => {
    setFields((prev) => prev.map((field) =>
      field.id === fieldId ? { ...field, followUps: [...(field.followUps || []), emptyFollowUp()] } : field
    ));
  };
  const updateFollowUp = (fieldId, followUpId, changes) => {
    setFields((prev) => prev.map((field) =>
      field.id === fieldId
        ? { ...field, followUps: field.followUps.map((f) => f.id === followUpId ? { ...f, ...changes } : f) }
        : field
    ));
  };
  const removeFollowUp = (fieldId, followUpId) => {
    setFields((prev) => prev.map((field) =>
      field.id === fieldId ? { ...field, followUps: field.followUps.filter((f) => f.id !== followUpId) } : field
    ));
  };

  const valid = name.trim() && fields.length > 0 &&
    fields.every((field) => {
      if (!field.label.trim()) return false;
      if (field.type === "select") {
        return field.optionsText.split(",").map((item) => item.trim()).filter(Boolean).length > 0;
      }
      if (field.type === "compliance") {
        return (field.followUps || []).every((f) => f.label.trim());
      }
      return true;
    });

  const handleSave = async () => {
    if (!valid) return;
    setSaving(true);
    setError(null);

    try {
      const body = {
        name,
        description,
        module_id: moduleId || null,
        fields: fields.map(({ optionsText, ...field }) => ({
          ...field,
          options: field.type === "select"
            ? optionsText.split(",").map((item) => item.trim()).filter(Boolean)
            : [],
          dateCalculation: field.type === "date" ? (field.dateCalculation || "") : "",
          followUps: field.type === "compliance" ? field.followUps : [],
        })),
      };

      const result = isEdit ? await api.updateInspection(inspection.id, body) : await api.createInspection(body);
      if (result.error) throw new Error(result.error);
      onSaved(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <motion.div
        className="modal modal-large"
        initial={{ opacity: 0, scale: 0.92, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.94, y: 12 }}
        transition={{ type: "spring", stiffness: 380, damping: 30, mass: 0.7 }}
      >
        <div className="modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: "#FEF3C7", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ClipboardCheck size={18} style={{ color: "#B45309" }} />
            </div>
            <h2>{isEdit ? "Editar inspeccion" : "Nueva inspeccion"}</h2>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="modal-form" style={{ maxHeight: "65vh", overflowY: "auto" }}>
          <div className="form-row two-col">
            <div>
              <label>Nombre de la inspeccion *</label>
              <input
                className="form-input"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Ej: Herramientas electricas - Taladro"
              />
            </div>
            <div>
              <label>Modulo</label>
              <select className="form-input" value={moduleId} onChange={(event) => setModuleId(event.target.value)}>
                <option value="">Sin modulo</option>
                {modules.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row">
            <label>Descripcion</label>
            <textarea
              className="form-input form-textarea"
              rows={2}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Que se evalua en esta inspeccion"
            />
          </div>

          <div className="form-row">
            <label>Preguntas de la inspeccion</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  style={{
                    border: "1px solid var(--border)",
                    borderRadius: 10,
                    padding: 12,
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    background: "var(--bg)",
                  }}
                >
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "0.72rem", color: "var(--text-muted)", flexShrink: 0 }}>#{index + 1}</span>
                    <input
                      className="form-input"
                      style={{ flex: "1 1 160px", minWidth: 0 }}
                      value={field.label}
                      placeholder="Etiqueta de la pregunta"
                      onChange={(event) => updateField(field.id, { label: event.target.value })}
                    />
                    <select
                      className="form-input"
                      style={{ flex: "1 1 160px", minWidth: 0, maxWidth: 230 }}
                      value={field.type}
                      onChange={(event) => updateField(field.id, {
                        type: event.target.value,
                        options: event.target.value === "select" ? field.options : [],
                        optionsText: event.target.value === "select" ? field.optionsText : "",
                        dateCalculation: event.target.value === "date" ? field.dateCalculation : "",
                        required: event.target.value === "compliance" ? true : field.required,
                      })}
                    >
                      {FIELD_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                    </select>
                    <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                      <button className="btn-icon" onClick={() => duplicateField(field.id)} title="Duplicar pregunta">
                        <Copy size={15} />
                      </button>
                      <button className="btn-icon" onClick={() => removeField(field.id)} disabled={fields.length === 1} title="Quitar pregunta">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>

                  <textarea
                    className="form-input form-textarea"
                    rows={2}
                    value={field.description || ""}
                    placeholder="Descripcion de ayuda para esta pregunta (opcional)"
                    onChange={(event) => updateField(field.id, { description: event.target.value })}
                  />

                  {field.type === "select" && (
                    <input
                      className="form-input"
                      value={field.optionsText}
                      placeholder="Opciones separadas por coma"
                      onChange={(event) => updateField(field.id, { optionsText: event.target.value })}
                    />
                  )}

                  {field.type === "compliance" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, background: "rgba(0,0,0,0.03)", borderRadius: 8, padding: 10 }}>
                      <p style={{ fontSize: "0.74rem", color: "var(--text-muted)" }}>
                        Opciones fijas: Cumple / No cumple / Cumple parcialmente / No aplica.
                        Si quieres pedir informacion adicional cuando la respuesta sea <b>No cumple</b> o <b>Cumple parcialmente</b>, agrega campos aqui abajo (libre, no limitado a fotos):
                      </p>
                      {(field.followUps || []).map((followUp) => (
                        <div key={followUp.id} style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                          <input
                            className="form-input"
                            style={{ flex: "1 1 200px", minWidth: 0 }}
                            value={followUp.label}
                            placeholder="Ej: Foto de la evidencia, Recomendacion, Detalle adicional..."
                            onChange={(event) => updateFollowUp(field.id, followUp.id, { label: event.target.value })}
                          />
                          <select
                            className="form-input"
                            style={{ flex: "1 1 130px", minWidth: 0, maxWidth: 150 }}
                            value={followUp.type}
                            onChange={(event) => updateFollowUp(field.id, followUp.id, { type: event.target.value })}
                          >
                            {FOLLOW_UP_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                          </select>
                          <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.74rem", color: "var(--text-muted)", whiteSpace: "nowrap", flexShrink: 0 }}>
                            <input
                              type="checkbox"
                              checked={followUp.required}
                              onChange={(event) => updateFollowUp(field.id, followUp.id, { required: event.target.checked })}
                            />
                            Obligatorio
                          </label>
                          <button className="btn-icon" onClick={() => removeFollowUp(field.id, followUp.id)} title="Quitar" style={{ flexShrink: 0 }}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                      <button className="btn-secondary" onClick={() => addFollowUp(field.id)} style={{ alignSelf: "flex-start" }}>
                        <Plus size={13} /> Agregar campo de seguimiento
                      </button>
                    </div>
                  )}

                  {field.type === "date" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <label style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>Cálculo automático</label>
                      <select
                        className="form-input"
                        value={field.dateCalculation || ""}
                        onChange={(event) => updateField(field.id, { dateCalculation: event.target.value })}
                      >
                        <option value="">Sin cálculo automático</option>
                        <option value="age">Calcular edad</option>
                        <option value="seniority">Calcular antigüedad</option>
                        <option value="elapsed">Calcular tiempo transcurrido</option>
                      </select>
                    </div>
                  )}

                  {field.type !== "compliance" && (
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.78rem", color: "var(--text-muted)", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(event) => updateField(field.id, { required: event.target.checked })}
                      />
                      Obligatorio
                    </label>
                  )}
                </div>
              ))}
            </div>
            <button className="btn-secondary" onClick={addField} style={{ marginTop: 10, alignSelf: "flex-start" }}>
              <Plus size={14} /> Agregar pregunta
            </button>
          </div>

          {duplicateNotice && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "#FEF3C7", borderRadius: 8, color: "#92400E", fontSize: "0.78rem" }}>
              <AlertCircle size={15} /> {duplicateNotice}
            </div>
          )}

          {error && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "#FEE2E2", borderRadius: 8, color: "#C53030", fontSize: "0.78rem" }}>
              <AlertCircle size={15} /> {error}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" onClick={handleSave} disabled={!valid || saving}>
            {saving ? <><Loader size={14} className="spin" /> Guardando...</> : "Guardar"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
